import { History } from 'app/graphql/__generated__/types/contento';
import Immutable from 'immutable';
import CONTENTO_TYPES from '../domain/ContentoTypes';
import env from '../env';
import ContinueWatching from './models/ContinueWatching';

// Helps calculate valid position
const SAVED_POSITION_RECAP_TIME = 5;
const MAX_ONGOING_ITEMS = 24;
const MAX_WATCHED_ITEMS = 1000;
const PERCENT_ENDED = 0.95;

export interface TypedContinueWatchingList extends Immutable.List<ContinueWatching> {
    toJS(): Array<ContinueWatching>;
}

const parsePosition = (position?: string | number): false | number => {
    if (!position) {
        return false;
    }

    if (typeof position === 'string') {
        return parseInt(position, 10);
    }

    return Math.floor(position);
};

export const createContentoHistory = (
    continueWatchingItems: TypedContinueWatchingList,
    useTitles: boolean = false
): Array<History> => {
    let history: Array<History> = [];

    if (continueWatchingItems && continueWatchingItems.size !== 0) {
        history = continueWatchingItems
            .toJS()
            .filter((item) => !item.deleted)
            .slice(0, env.HISTORY_UPPER_LIMIT)
            .map((continueWatchingItem) => ({
                id: useTitles ? continueWatchingItem.titleArticleId : continueWatchingItem.videoId,
                progressSeconds: parsePosition(continueWatchingItem.position) || 0,
                progressFraction: null,
            }));
    }

    return history;
};

const sortByDate = (a: ContinueWatching, b: ContinueWatching): number => b.timestamp - a.timestamp;

const setEndTime = (position: number, videoLength: number, itemToSave: ContinueWatching): void => {
    const ended = position / videoLength > PERCENT_ENDED;

    /* eslint-disable no-param-reassign */
    if (ended) {
        // eslint-disable-next-line no-param-reassign
        itemToSave.ended = true;
        itemToSave.position = videoLength;
    } else {
        itemToSave.ended = false;

        // Move position 5 seconds earlier for better user experience. Round to whole number
        itemToSave.position =
            position > SAVED_POSITION_RECAP_TIME ? Math.floor(position - SAVED_POSITION_RECAP_TIME) : 0;
    }
};

const injectItemToSave = (items: Array<ContinueWatching>, itemToSave: ContinueWatching): void => {
    const indexOfItem = items.findIndex((item) => item.videoId === itemToSave.videoId);

    if (indexOfItem > -1) {
        // Position already saved for this video
        items.splice(indexOfItem, 1);
    }
    items.push(itemToSave);
};

export const addPosition = (
    barn: boolean,
    items: Array<ContinueWatching>,
    svtId: string,
    titleArticleId: string,
    __typename: string,
    position: number,
    videoLength: number
): Array<ContinueWatching> => {
    const isMovie = __typename === CONTENTO_TYPES.SINGLE;

    const itemToSave: ContinueWatching = {
        barn,
        timestamp: new Date().getTime(),
        videoId: svtId,
        titleArticleId,
        movie: isMovie,
    };

    setEndTime(position, videoLength, itemToSave);

    injectItemToSave(items, itemToSave);

    // Only save x number of ongoing videos
    let ongoing = items.filter((item) => item.ended === false).sort(sortByDate);
    ongoing = ongoing.slice(0, MAX_ONGOING_ITEMS);

    // Limit the number of watched videos
    let watched = items.filter((item) => item.ended === true).sort(sortByDate);
    watched = watched.slice(0, MAX_WATCHED_ITEMS);

    return ongoing.concat(watched).sort(sortByDate);
};

export const removeItems = (
    items: Array<ContinueWatching>,
    itemsToRemove: Array<ContinueWatching>
): Array<ContinueWatching> =>
    items.filter((item) => {
        // Direct hit
        if (itemsToRemove.find((itemToRemove) => itemToRemove.videoId === item.videoId)) {
            return false;
        }

        if (!item.titleArticleId || item.movie) {
            return true;
        }

        if (
            itemsToRemove.find(
                (itemToRemove) => String(itemToRemove.titleArticleId) === String(item.titleArticleId)
            )
        ) {
            return false;
        }

        return item;
    });
